import { use } from 'react';
import {
  Box,
  BoxProps,
  createVarsResolver,
  DataAttributes,
  extractStyleProps,
  getFontSize,
  getRadius,
  getSize,
  MantineRadius,
  MantineSize,
  polymorphicFactory,
  PolymorphicFactory,
  rem,
  StylesApiProps,
  useProps,
  useStyles,
} from '../../core';
import { Loader } from '../Loader/Loader';
import { InputContext } from './Input.context';
import {
  InputClearButton,
  type InputClearButtonProps,
  type InputClearButtonFactory,
} from './InputClearButton/InputClearButton';
import {
  ClearSectionMode,
  InputClearSection,
  type InputClearSectionProps,
} from './InputClearSection/InputClearSection';
import {
  InputDescription,
  type InputDescriptionProps,
  type InputDescriptionStylesNames,
  type InputDescriptionCssVariables,
  type InputDescriptionFactory,
} from './InputDescription/InputDescription';
import {
  InputError,
  type InputErrorProps,
  type InputErrorStylesNames,
  type InputErrorCssVariables,
  type InputErrorFactory,
} from './InputError/InputError';
import {
  InputLabel,
  type InputLabelProps,
  type InputLabelStylesNames,
  type InputLabelCssVariables,
  type InputLabelFactory,
} from './InputLabel/InputLabel';
import {
  InputPlaceholder,
  type InputPlaceholderProps,
  type InputPlaceholderStylesNames,
  type InputPlaceholderFactory,
} from './InputPlaceholder/InputPlaceholder';
import { InputSuccess } from './InputSuccess/InputSuccess';
import { InputWrapperContext } from './InputWrapper.context';
import {
  __InputWrapperProps,
  InputWrapper,
  InputWrapperStylesNames,
  type InputWrapperProps,
  type InputWrapperFactory,
} from './InputWrapper/InputWrapper';
import classes from './Input.module.css';
// Required to be a separate type for docgen script
type WrapperProps = React.ComponentProps<'div'> & DataAttributes;

export interface __BaseInputProps extends __InputWrapperProps, __InputProps {
  /** Props passed down to the root element */
  wrapperProps?: WrapperProps;
}

export type __InputStylesNames = InputStylesNames | InputWrapperStylesNames;

export type InputStylesNames = 'input' | 'wrapper' | 'section' | 'bottomSection';
export type InputVariant = 'default' | 'filled' | 'unstyled';
export type InputCssVariables = {
  wrapper:
    | '--input-height'
    | '--input-fz'
    | '--input-radius'
    | '--input-left-section-width'
    | '--input-right-section-width'
    | '--input-left-section-pointer-events'
    | '--input-right-section-pointer-events'
    | '--input-padding-y'
    | '--input-margin-top'
    | '--input-margin-bottom';
};

export interface InputStylesCtx {
  offsetTop: boolean | undefined;
  offsetBottom: boolean | undefined;
}

export interface __InputProps {
  /** Content section displayed on the left side of the input */
  leftSection?: React.ReactNode;

  /** Left section width, used to set `width` of the section and input `padding-left`, by default equals to the input height */
  leftSectionWidth?: React.CSSProperties['width'];

  /** Props passed down to the `leftSection` element */
  leftSectionProps?: React.ComponentProps<'div'>;

  /** Sets `pointer-events` styles on the `leftSection` element. Use `'all'` when section contains interactive elements (buttons, links). @default 'none' */
  leftSectionPointerEvents?: React.CSSProperties['pointerEvents'];

  /** Content section displayed on the right side of the input */
  rightSection?: React.ReactNode;

  /** Right section width, used to set `width` of the section and input `padding-right`, by default equals to the input height */
  rightSectionWidth?: React.CSSProperties['width'];

  /** Props passed down to the `rightSection` element */
  rightSectionProps?: React.ComponentProps<'div'>;

  /** Sets `pointer-events` styles on the `rightSection` element. Use `'all'` when section contains interactive elements (buttons, links). @default 'none' */
  rightSectionPointerEvents?: React.CSSProperties['pointerEvents'];

  /** Sets `required` attribute on the `input` element */
  required?: boolean;

  /** Key of `theme.radius` or any valid CSS value to set `border-radius`, numbers are converted to rem @default theme.defaultRadius */
  radius?: MantineRadius;

  /** Sets `disabled` attribute on the `input` element */
  disabled?: boolean;

  /** Controls input `height`, horizontal `padding`, and `font-size` @default 'sm' */
  size?: MantineSize | (string & {});

  /** Determines whether the input should have `cursor: pointer` style. Use when input acts as a button-like trigger (e.g., `component="button"` for Select/DatePicker). @default false */
  pointer?: boolean;

  /** Determines whether the input should have red border and red text color when the `error` prop is set @default true */
  withErrorStyles?: boolean;

  /** Determines whether the input should have green border when the `success` prop is set @default true */
  withSuccessStyles?: boolean;

  /** HTML `size` attribute for the input element (number of visible characters) */
  inputSize?: string;

  /** Section to be displayed when the input is `__clearable` and `rightSection` is not defined */
  __clearSection?: React.ReactNode;

  /** Determines whether the `__clearSection` should be displayed if it is passed to the component, has no effect if `rightSection` is defined */
  __clearable?: boolean;

  /** Determines how the clear button and rightSection are rendered @default 'both' */
  __clearSectionMode?: ClearSectionMode;

  /** Right section displayed when both `__clearSection` and `rightSection` are not defined */
  __defaultRightSection?: React.ReactNode;

  /** Displays loading indicator in the left or right section @default false */
  loading?: boolean;

  /** Position of the loading indicator @default 'right' */
  loadingPosition?: 'left' | 'right';

  /** Content section displayed at the bottom of the input, inside the border */
  __bottomSection?: React.ReactNode;

  /** Props passed down to the `__bottomSection` element */
  __bottomSectionProps?: React.ComponentProps<'div'>;
}

export interface InputProps extends BoxProps, __InputProps, StylesApiProps<InputFactory> {
  __staticSelector?: string;

  /** Props passed to Styles API context, replaces `Input.Wrapper` props */
  __stylesApiProps?: Record<string, any>;

  /** Determines whether the input should have error styles and `aria-invalid` attribute */
  error?: React.ReactNode;

  /** Determines whether the input should have success styles */
  success?: React.ReactNode;

  /** Adjusts padding and sizing calculations for multiline inputs (use with `component="textarea"`). Does not make the input multiline by itself. @default false */
  multiline?: boolean;

  /** Input element id */
  id?: string;

  /** Determines whether `aria-` and other accessibility attributes should be added to the input. Only disable when implementing custom accessibility handling. @default true */
  withAria?: boolean;

  /** Props passed down to the root element of the `Input` component */
  wrapperProps?: WrapperProps;

  /** Root element ref */
  rootRef?: React.Ref<HTMLDivElement>;
}

export type InputFactory = PolymorphicFactory<{
  props: InputProps;
  defaultRef: HTMLInputElement;
  defaultComponent: 'input';
  stylesNames: InputStylesNames;
  variant: InputVariant;
  vars: InputCssVariables;
  ctx: InputStylesCtx;
  staticComponents: {
    Label: typeof InputLabel;
    Error: typeof InputError;
    Success: typeof InputSuccess;
    Description: typeof InputDescription;
    Placeholder: typeof InputPlaceholder;
    Wrapper: typeof InputWrapper;
    ClearButton: typeof InputClearButton;
  };
}>;

const defaultProps = {
  variant: 'default',
  leftSectionPointerEvents: 'none',
  rightSectionPointerEvents: 'none',
  withAria: true,
  withErrorStyles: true,
  withSuccessStyles: true,
  size: 'sm',
  loading: false,
  loadingPosition: 'right',
} satisfies Partial<InputProps>;

const varsResolver = createVarsResolver<InputFactory>((_, props, ctx) => ({
  wrapper: {
    '--input-margin-top': ctx.offsetTop ? 'calc(var(--mantine-spacing-xs) / 2)' : undefined,
    '--input-margin-bottom': ctx.offsetBottom ? 'calc(var(--mantine-spacing-xs) / 2)' : undefined,
    '--input-height': getSize(props.size, 'input-height'),
    '--input-fz': getFontSize(props.size),
    '--input-radius': props.radius === undefined ? undefined : getRadius(props.radius),
    '--input-left-section-width':
      props.leftSectionWidth !== undefined ? rem(props.leftSectionWidth) : undefined,
    '--input-right-section-width':
      props.rightSectionWidth !== undefined ? rem(props.rightSectionWidth) : undefined,
    '--input-padding-y': props.multiline ? getSize(props.size, 'input-padding-y') : undefined,
    '--input-left-section-pointer-events': props.leftSectionPointerEvents,
    '--input-right-section-pointer-events': props.rightSectionPointerEvents,
  },
}));

export const Input = polymorphicFactory<InputFactory>((_props) => {
  const props = useProps('Input', defaultProps, _props);
  const {
    classNames,
    className,
    style,
    styles,
    unstyled,
    required,
    __staticSelector,
    __stylesApiProps,
    size,
    wrapperProps,
    error,
    success,
    disabled,
    leftSection,
    leftSectionProps,
    leftSectionWidth,
    rightSection,
    rightSectionProps,
    rightSectionWidth,
    rightSectionPointerEvents,
    leftSectionPointerEvents,
    variant,
    vars,
    pointer,
    multiline,
    radius,
    id,
    withAria,
    withErrorStyles,
    withSuccessStyles,
    mod,
    inputSize,
    attributes,
    __clearSection,
    __clearable,
    __clearSectionMode,
    __defaultRightSection,
    loading,
    loadingPosition,
    __bottomSection,
    __bottomSectionProps,
    rootRef,
    dir,
    ...others
  } = props as typeof props & Pick<React.HTMLAttributes<HTMLDivElement>, 'dir'>;

  const { styleProps, rest } = extractStyleProps(others);
  const ctx = use(InputWrapperContext);
  const stylesCtx: InputStylesCtx = { offsetBottom: ctx?.offsetBottom, offsetTop: ctx?.offsetTop };

  const getStyles = useStyles<InputFactory>({
    name: ['Input', __staticSelector],
    props: __stylesApiProps || props,
    classes,
    className,
    style,
    classNames,
    styles,
    unstyled,
    attributes,
    stylesCtx,
    rootSelector: 'wrapper',
    vars,
    varsResolver,
  });

  const ariaAttributes = withAria
    ? {
        required,
        disabled,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': ctx?.describedBy,
        id: ctx?.inputId || id,
      }
    : {};

  const loadingIndicator = loading ? (
    <Loader
      size={
        loadingPosition === 'left'
          ? 'calc(var(--input-left-section-size) / 2)'
          : 'calc(var(--input-right-section-size) / 2)'
      }
    />
  ) : null;

  const _leftSection = loading && loadingPosition === 'left' ? loadingIndicator : leftSection;
  const _rightSection: React.ReactNode = InputClearSection({
    __clearable,
    __clearSection,
    rightSection: loading && loadingPosition === 'right' ? loadingIndicator : rightSection,
    __defaultRightSection,
    size,
    __clearSectionMode,
  });

  return (
    <InputContext value={{ size: size || 'sm' }}>
      <Box
        ref={rootRef}
        dir={dir}
        {...getStyles('wrapper')}
        {...styleProps}
        {...wrapperProps}
        mod={[
          {
            error: !!error && withErrorStyles,
            success: !!success && !error && withSuccessStyles,
            pointer,
            disabled,
            multiline,
            withRightSection: !!_rightSection,
            withLeftSection: !!_leftSection,
            withBottomSection: !!__bottomSection,
          },
          mod,
        ]}
        variant={variant}
        size={size}
      >
        {_leftSection && (
          <div
            {...leftSectionProps}
            data-position="left"
            {...getStyles('section', {
              className: leftSectionProps?.className,
              style: leftSectionProps?.style,
            })}
          >
            {_leftSection}
          </div>
        )}

        <Box
          component="input"
          {...rest}
          {...ariaAttributes}
          required={required}
          mod={{
            disabled,
            error: !!error && withErrorStyles,
            success: !!success && !error && withSuccessStyles,
          }}
          variant={variant}
          __size={inputSize}
          {...getStyles('input')}
        />

        {__bottomSection && (
          <div
            {...__bottomSectionProps}
            {...getStyles('bottomSection', {
              className: __bottomSectionProps?.className,
              style: __bottomSectionProps?.style,
            })}
          >
            {__bottomSection}
          </div>
        )}

        {_rightSection && (
          <div
            {...rightSectionProps}
            data-position="right"
            {...getStyles('section', {
              className: rightSectionProps?.className,
              style: rightSectionProps?.style,
            })}
          >
            {_rightSection}
          </div>
        )}
      </Box>
    </InputContext>
  );
});

Input.classes = classes;
Input.varsResolver = varsResolver;
Input.Wrapper = InputWrapper;
Input.Label = InputLabel;
Input.Error = InputError;
Input.Success = InputSuccess;
Input.Description = InputDescription;
Input.Placeholder = InputPlaceholder;
Input.ClearButton = InputClearButton;
Input.displayName = '@mantine/core/Input';

export namespace Input {
  export type Props = InputProps;
  export type StylesNames = InputStylesNames;
  export type CssVariables = InputCssVariables;
  export type Factory = InputFactory;

  export namespace Wrapper {
    export type Props = InputWrapperProps;
    export type StylesNames = InputWrapperStylesNames;
    export type Factory = InputWrapperFactory;
  }

  export namespace Description {
    export type Props = InputDescriptionProps;
    export type StylesNames = InputDescriptionStylesNames;
    export type CssVariables = InputDescriptionCssVariables;
    export type Factory = InputDescriptionFactory;
  }

  export namespace Error {
    export type Props = InputErrorProps;
    export type StylesNames = InputErrorStylesNames;
    export type CssVariables = InputErrorCssVariables;
    export type Factory = InputErrorFactory;
  }

  export namespace Label {
    export type Props = InputLabelProps;
    export type StylesNames = InputLabelStylesNames;
    export type CssVariables = InputLabelCssVariables;
    export type Factory = InputLabelFactory;
  }

  export namespace Placeholder {
    export type Props = InputPlaceholderProps;
    export type StylesNames = InputPlaceholderStylesNames;
    export type Factory = InputPlaceholderFactory;
  }

  export namespace ClearButton {
    export type Props = InputClearButtonProps;
    export type Factory = InputClearButtonFactory;
  }

  export namespace ClearSection {
    export type Props = InputClearSectionProps;
    export type Mode = ClearSectionMode;
  }
}
